import { AxiosResponse } from 'axios'

import { useCallback, useState } from 'react'

import { Alert } from '@/modules'

import { useBaseAPI } from './baseApi.hook'

export type Headers = { [key: string]: string }

export type UploadProgress = {
  loaded: number
  total?: number
}

export type RequestOptions = {
  headers?: Headers
  onUploadProgress?: (progressEvent: UploadProgress) => void
  params?: Record<string, string | number>
}

export enum CommandMethods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export interface UseCommandOutput<T> {
  isRunning: boolean
  data?: T
  error?: Error
  run: <I>(input: RunCommandInput<I, T>, options?: RequestOptions) => void
  abort: () => void
}

type Bind = [string, string]

export interface RunCommandInput<I, O> {
  input?: I
  binds?: Bind[]
  onSuccess?: (data: O) => void
  onFail?: (error: Error) => void
}

export function useCommand<T>(method: CommandMethods, url: string): UseCommandOutput<T> {
  const { instance, canceler } = useBaseAPI()

  const [isRunning, setRunning] = useState<boolean>(false)
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)

  const { sendAlert } = Alert.Hook()

  const run = useCallback(
    <I>({ input, binds, onSuccess, onFail }: RunCommandInput<I, T>, options?: RequestOptions): void => {
      setRunning(true)
      setError(undefined)
      setData(undefined)

      let finalUrl = url
      if (binds?.length) {
        binds.forEach((bind: Bind) => {
          finalUrl = finalUrl.replace(`:${bind[0]}`, bind[1])
        })
      }

      instance({
        method,
        url: finalUrl,
        data: input,
        headers: {
          ...options?.headers,
        },
        params: options?.params,
        onUploadProgress: options?.onUploadProgress,
      })
        .then(({ data }: AxiosResponse<T>) => {
          setData(data)
          if (onSuccess) onSuccess(data)
        })
        .catch((error: Error) => {
          setError(error)
          setData(undefined)
          if (onFail) {
            onFail(error)
          }
        })
        .finally(() => {
          setRunning(false)
        })
    },
    [instance, url, method, sendAlert],
  )

  return {
    isRunning,
    data: data,
    error,
    run,
    abort: canceler.abort,
  }
}
